import React, { useEffect, useState, useCallback } from "react";
import { TL } from "../i18n";
import client, { apiErr } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, Button, Input, Select, Badge } from "./ui";
import { Wrench, RefreshCw } from "lucide-react";

const APPS = [
  { v: "family_tree", l: "Family Tree" },
  { v: "holdings", l: "Holdings Management" },
  { v: "currency", l: "Currency Management" },
  { v: "benefits", l: "Benefits Management" },
];
const STATUS_COLOR = { pending: "info", applied: "success", dismissed: "neutral", superseded: "warning" };

export default function RepairDispatch() {
  const { isAdmin } = useApp();
  const [runners, setRunners] = useState([]);
  const [ownerId, setOwnerId] = useState("");
  const [app, setApp] = useState("family_tree");
  const [note, setNote] = useState("");
  const [history, setHistory] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    client.get("/admin/runners").then((r) => setRunners(r.data.runners || r.data || [])).catch(() => {});
    client.get("/support/repair/history").then((r) => setHistory(r.data.directives || [])).catch(() => {});
  }, []);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);
  if (!isAdmin) return null;

  const dispatch = async () => {
    if (!ownerId) { setMsg("Select a runner first."); return; }
    setBusy(true); setMsg("");
    try {
      await client.post("/support/repair/dispatch", { owner_id: ownerId, app, note });
      setMsg("Repair dispatched — the runner will see a one-click restore in their app.");
      setNote(""); load(); setTimeout(() => setMsg(""), 3000);
    } catch (e) { setMsg(apiErr(e)); }
    setBusy(false);
  };

  return (
    <Card className="p-5" data-testid="repair-dispatch-panel">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Wrench size={18} className="text-[var(--primary)]" />
        <h3 className="font-head text-lg">{TL("Remote repair")}</h3>
        <Badge color="info">{TL("support tool")}</Badge>
        {msg && <span className="text-xs text-[var(--primary)]" data-testid="repair-dispatch-msg">{msg}</span>}
      </div>
      <p className="mb-4 text-xs opacity-60">If a runner reports a broken or corrupted instance, dispatch a repair. They receive a one-click
        "Restore / repair now" that recovers their most recent data from the server-side live backup — scoped to their own account only.</p>
      <div className="grid gap-3 md:grid-cols-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium opacity-70">{TL("Runner")}</span>
          <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} data-testid="repair-runner-select"
            className="w-full rounded-md border border-black/15 bg-white/60 px-3 py-2 text-sm outline-none dark:border-white/15 dark:bg-black/30">
            <option value="">{TL("— select runner —")}</option>
            {runners.map((r) => <option key={r.id} value={r.id}>{r.name || r.email || r.username || r.id}</option>)}
          </select>
        </label>
        <Select label={TL("Application")} value={app} onChange={(e) => setApp(e.target.value)} data-testid="repair-app-select">
          {APPS.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
        </Select>
        <Input label={TL("Note to runner (optional)")} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder={TL("e.g. This restores your Benefits data to this morning.")} data-testid="repair-note" />
      </div>
      <div className="mt-3">
        <Button onClick={dispatch} disabled={busy} data-testid="repair-dispatch-btn">
          <RefreshCw size={14} className={`me-1.5 ${busy ? "animate-spin" : ""}`} /> Dispatch repair
        </Button>
      </div>

      <div className="mt-5">
        <div className="mb-1 text-sm font-semibold opacity-80">{TL("Recent repairs")}</div>
        {history.length === 0 ? <p className="text-xs opacity-60">{TL("No repairs dispatched yet.")}</p> : (
          <table className="w-full text-xs" data-testid="repair-history-table">
            <thead><tr className="text-start opacity-60"><th className="py-1">{TL("When")}</th><th>{TL("Runner")}</th><th>{TL("App")}</th><th>{TL("By")}</th><th>{TL("Status")}</th></tr></thead>
            <tbody>
              {history.map((d) => (
                <tr key={d.id} className="border-t border-black/5 dark:border-white/5" data-testid={`repair-hist-${d.id}`}>
                  <td className="py-1">{(d.created_at || "").slice(5, 16).replace("T", " ")}</td>
                  <td>{d.target_name}</td>
                  <td>{d.app_label}</td>
                  <td>{d.created_by}</td>
                  <td><Badge color={STATUS_COLOR[d.status] || "neutral"}>{d.status}{d.status === "applied" && d.restored != null ? ` · ${d.restored} restored` : ""}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}
